import { createTheme, alpha } from '@mui/material/styles'

// Centralized app colors - import this in any styles file
export const appColors = {
  page: {
    background: '#F3F5F7',
    cardBg: '#FFFFFF',
    cardRadius: '16px',
  },
  button: {
    primary: '#0B51C5',
    primaryHover: '#0842a0',
    primaryText: '#FFFFFF',
    secondary: '#DEDEDE',
    secondaryHover: '#d0d0d0',
    secondaryText: '#404040',
    tertiary: '#475569',
    tertiaryHover: '#374151',
    tertiaryText: '#FFFFFF',
  },
  table: {
    headerBg: '#000000',
    headerText: '#FFFFFF',
    bodyText: '#334155',
    rowHover: '#F1F5F9',
    border: '#E2E8F0',
    emptyText: '#64748B',
    containerBg: '#FFFFFF',
    containerRadius: '8px',
  },
  border: '#E2E8F0',
}

const palette = {
  primary: {
    main: '#2563EB',
    light: '#3B82F6',
    dark: '#1D4ED8',
    contrastText: '#FFFFFF',
    50: '#EFF6FF',
    100: '#DBEAFE',
    600: '#2563EB',
    700: '#1D4ED8',
  },
  secondary: {
    main: '#475569',
    light: '#64748B',
    dark: '#334155',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#DC2626',
    light: '#FEE2E2',
    dark: '#B91C1C',
  },
  warning: {
    main: '#F59E0B',
    light: '#FEF3C7',
    dark: '#D97706',
  },
  success: {
    main: '#16A34A',
    light: '#DCFCE7',
    dark: '#15803D',
  },
  info: {
    main: '#0EA5E9',
    light: '#E0F2FE',
    dark: '#0284C7',
  },
  grey: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },
  background: {
    default: '#F8FAFC',
    paper: '#FFFFFF',
  },
  text: {
    primary: '#0F172A',
    secondary: '#475569',
    disabled: '#94A3B8',
  },
  action: {
    hover: 'rgba(37, 99, 235, 0.04)',
    selected: 'rgba(37, 99, 235, 0.08)',
    focus: 'rgba(37, 99, 235, 0.12)',
    disabledBackground: '#F1F5F9',
  },
  divider: '#E2E8F0',
  sidebar: {
    background: '#000000',
    text: '#CBD5E1',
    textActive: '#FFFFFF',
    activeItem: '#2563EB',
    hoverItem: '#1E293B',
    border: '#1E293B',
  },
  navbar: {
    background: '#FFFFFF',
    text: '#0F172A',
    border: '#E2E8F0',
  },
  surface: {
    search: '#F1F5F9',
    filterBar: '#F1F5F9',
    card: '#FFFFFF',
    elevated: '#FFFFFF',
  },
};

const theme = createTheme({
  palette,
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h1: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.025em' },
    h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.025em' },
    h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5 },
    overline: { fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.08em', textTransform: 'uppercase' },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.5 },
  },
  shape: { borderRadius: 8 },
  spacing: 8,
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0,0,0,0.05)',
    '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)',
    '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
    '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
    '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    ...Array(18).fill('none'),
  ],
  breakpoints: {
    values: { xs: 0, sm: 640, md: 1024, lg: 1280, xl: 1536 },
  },
  transitions: {
    duration: { shortest: 120, shorter: 160, short: 200, standard: 250, complex: 300 },
    easing: { easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)', easeOut: 'cubic-bezier(0, 0, 0.2, 1)', easeIn: 'cubic-bezier(0.4, 0, 1, 1)' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': { boxSizing: 'border-box' },
        body: { overscrollBehavior: 'none' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          fontWeight: 500,
          fontSize: '0.875rem',
          lineHeight: 1.5,
          transition: 'all 0.2s ease',
        },
        sizeSmall: { height: '32px', padding: '4px 12px', fontSize: '0.8125rem' },
        sizeMedium: { height: '36px', padding: '6px 16px' },
        sizeLarge: { height: '40px', padding: '8px 20px' },
        contained: {
          boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
          '&:hover': { boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)' },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
          '&:hover': { background: `linear-gradient(135deg, ${palette.primary.light} 0%, ${palette.primary.main} 100%)` },
        },
        outlined: {
          borderColor: palette.grey[300],
          color: palette.text.primary,
          '&:hover': { backgroundColor: palette.grey[50], borderColor: palette.grey[400] },
        },
        text: { '&:hover': { backgroundColor: alpha(palette.primary.main, 0.04) } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: '8px', transition: 'all 0.15s ease' },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: `1px solid ${palette.divider}`,
          backgroundImage: 'none',
        },
        elevation1: { boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)' },
        elevation2: { boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: `1px solid ${palette.divider}`,
          transition: 'all 0.2s ease',
          '&:hover': { borderColor: palette.grey[300], boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)' },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: palette.background.paper,
            transition: 'all 0.15s ease',
            '& fieldset': { borderColor: palette.grey[300] },
            '&:hover fieldset': { borderColor: palette.grey[400] },
            '&.Mui-focused fieldset': { borderColor: palette.primary.main, borderWidth: '1.5px' },
          },
          '& .MuiFilledInput-root': {
            borderRadius: '8px',
            backgroundColor: palette.grey[50],
            '&:hover': { backgroundColor: palette.grey[100] },
            '&.Mui-focused': { backgroundColor: palette.grey[50] },
            '&::before, &::after': { display: 'none' },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { fontSize: '0.875rem', borderRadius: '8px' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          minHeight: '40px',
          padding: '8px 16px',
          color: palette.text.secondary,
          '&.Mui-selected': { color: palette.primary.main, fontWeight: 600 },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: '2px', borderRadius: '1px' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: '6px', fontWeight: 500, fontSize: '0.75rem' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.grey[900],
          fontSize: '0.75rem',
          padding: '6px 12px',
          borderRadius: '6px',
        },
        arrow: { color: palette.grey[900] },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '10px',
          border: `1px solid ${palette.divider}`,
          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
          marginTop: '4px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '8px 16px',
          borderRadius: '6px',
          margin: '2px 6px',
          '&:hover': { backgroundColor: palette.grey[50] },
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: { '& .MuiBackdrop-root': { backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)' } },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: '16px', border: 'none', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: '4px', height: '3px', backgroundColor: palette.grey[100] },
        bar: { borderRadius: '4px' },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: palette.divider },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontSize: '0.8125rem', fontWeight: 600 },
      },
    },
    MuiTabScrollButton: {
      styleOverrides: { root: { width: '28px' } },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: { color: palette.grey[400] },
      },
    },
  },
});

export default theme;
